import type {
  ApiUpdate,
  ApiUpdateAuthorizationError,
  ApiUpdateAuthorizationState,
  ApiUpdateConnectionState,
  ApiUpdateCurrentUser,
  ApiUpdatePasskeyOption,
  ApiUpdateServerTimeOffset,
  ApiUpdateSession,
  ApiUpdateUserAlreadyAuthorized,
} from '../../../api/types';
import type { LangCode } from '../../../types';
import type { RequiredGlobalActions } from '../../index';
import type { ActionReturnType, GlobalState } from '../../types';

import { initDebugConsole } from '../../../util/debugConsole';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { getShippingError, shouldClosePaymentModal } from '../../../util/getReadableErrorText';
import { unique } from '../../../util/iteratees';
import { getAccountsInfo, getAccountSlotUrl } from '../../../util/multiaccount';
import { oldSetLanguage } from '../../../util/oldLangProvider';
import { clearWebTokenAuth } from '../../../util/routing';
import { isWorkGoogleBridgeCallback } from '../../../util/googleAuth';
import {
  buildStorageSessionRequestBody,
  captureSaasHandoffBootstrapFromSearch,
} from '../../../util/saasHandoffIntent';
import { clearPendingWebAuthToken } from '../../../util/webAuthTokenBootstrap';
import { clearPreferredAuthRestartMode } from '../../../util/preferredAuthRestartMode';
import {
  isInternalWebchatHttpFallbackEligible,
  markInternalWebchatHttpFallback,
  shouldUseInternalWebchatHttpFallback,
} from '../../../util/internalWebchatHttpFallback';
import {
  isPublicWebchatHttpFallbackEligible,
  markPublicWebchatHttpFallback,
  shouldUsePublicWebchatHttpFallback,
} from '../../../util/publicWebchatHttpFallback';
import { setServerTimeOffset } from '../../../util/serverTime';
import { ACCOUNT_SLOT } from '../../../util/multiaccount';
import { clearCurrentSlotAuthKeysOnly, clearStoredAuthKeysOnly, clearStoredSession, updateSessionUserId } from '../../../util/sessions';
import { DEBUG_PHONE_AUTH_MARKER } from '../../../util/debugLoginMarkers';
import { logTranslationDebugEvent, shouldForceTranslationDebugForUser } from '../../../util/translationDebug';
import { forceWebsync } from '../../../util/websync';
import { reportClientError } from '../../../util/clientErrorReporter';
import { clearSmsLoginStuckTimer, reportAuthSessionEvent, reportSmsLoginEvent } from '../../../util/authObservability';
import { callApi, setShouldEnableDebugLog } from '../../../api/gramjs';
import { degradeSyncFlagsForConnection } from '../api/syncState';
import {
  CONNECTING_SELF_HEAL_DELAY_MS,
  shouldTriggerConnectingSelfHeal,
} from '../api/connectionSelfHeal';
import { reportConnectionStateTransition } from '../api/connectionEvidenceLog';
import { isChatChannel, isChatSuperGroup } from '../../helpers';
import {
  addActionHandler, getActions, getGlobal, setGlobal,
} from '../../index';
import { flushPendingMessageReactionLoads } from '../api/reactions';
import { updateUser, updateUserFullInfo } from '../../reducers';
import { updateAuth } from '../../reducers/auth';
import { updateSharedState } from '../../reducers/sharedState';
import { updateTabState } from '../../reducers/tabs';
import { selectTabState } from '../../selectors';
import { selectTranslationLanguage } from '../../selectors/settings';
import { selectSharedSettings } from '../../selectors/sharedState';
import { selectUser } from '../../selectors/users';

const MAX_BROKEN_RECONNECT_ATTEMPTS = 10;
const POST_AUTH_SYNC_PROBE_DELAY_MS = 2_000;
const SAAS_HANDOFF_AUTH_RESET_KEY = 'runai_saas_handoff_auth_reset_done';
let brokenReconnectAttempts = 0;
let connectingSelfHealAttempts = 0;
let connectingSince: number | undefined;
let connectingSelfHealTimeout: ReturnType<typeof setTimeout> | undefined;
let brokenReconnectTimeout: ReturnType<typeof setTimeout> | undefined;
let postAuthSyncProbeTimeout: ReturnType<typeof setTimeout> | undefined;
let publicWebchatBrokenStartupCount = 0;
let publicWebchatBrokenStartupWindowStartedAt: number | undefined;
let internalWebchatBrokenReconnectCount = 0;
let internalWebchatBrokenReconnectWindowStartedAt: number | undefined;
let hasAttemptedStorageSessionRepair = false;

captureSaasHandoffBootstrapFromSearch();

function clearConnectingSelfHealTimer() {
  if (connectingSelfHealTimeout !== undefined) {
    clearTimeout(connectingSelfHealTimeout);
    connectingSelfHealTimeout = undefined;
  }
}

function clearBrokenReconnectTimer() {
  if (brokenReconnectTimeout !== undefined) {
    clearTimeout(brokenReconnectTimeout);
    brokenReconnectTimeout = undefined;
  }
}

function clearPostAuthSyncProbeTimer() {
  if (postAuthSyncProbeTimeout !== undefined) {
    clearTimeout(postAuthSyncProbeTimeout);
    postAuthSyncProbeTimeout = undefined;
  }
}

function emitReconnectTrace(transition: string, details: Record<string, unknown> = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  const debugWindow = window as typeof window & {
    __runaiDebugLogs?: Array<{ level: string; args: any[]; ts: number }>;
  };
  debugWindow.__runaiDebugLogs ||= [];
  debugWindow.__runaiDebugLogs.push({
    level: 'info',
    args: ['[reconnect-trace]', { transition, ...details }],
    ts: Date.now(),
  });
}

function resetConnectingSelfHealState() {
  clearConnectingSelfHealTimer();
  connectingSelfHealAttempts = 0;
  connectingSince = undefined;
}

function notePublicWebchatBrokenStartup(now = Date.now()) {
  const WINDOW_MS = 15_000;

  if (
    publicWebchatBrokenStartupWindowStartedAt === undefined
    || now - publicWebchatBrokenStartupWindowStartedAt > WINDOW_MS
  ) {
    publicWebchatBrokenStartupWindowStartedAt = now;
    publicWebchatBrokenStartupCount = 0;
  }

  publicWebchatBrokenStartupCount += 1;
  return publicWebchatBrokenStartupCount >= 2;
}

function noteInternalWebchatBrokenReconnect(now = Date.now()) {
  const WINDOW_MS = 15_000;

  if (
    internalWebchatBrokenReconnectWindowStartedAt === undefined
    || now - internalWebchatBrokenReconnectWindowStartedAt > WINDOW_MS
  ) {
    internalWebchatBrokenReconnectWindowStartedAt = now;
    internalWebchatBrokenReconnectCount = 0;
  }

  internalWebchatBrokenReconnectCount += 1;
  return internalWebchatBrokenReconnectCount >= 2;
}

function isPublicSaasOwnerAccessTokenLane(search = window.location.search) {
  try {
    const searchParams = new URLSearchParams(search);
    return searchParams.get('run_source') === 'saas'
      && Boolean(searchParams.get('ownerAccessToken')?.trim());
  } catch {
    return false;
  }
}

function resetPublicWebchatBrokenStartupState() {
  publicWebchatBrokenStartupCount = 0;
  publicWebchatBrokenStartupWindowStartedAt = undefined;
}

function resetInternalWebchatBrokenReconnectState() {
  internalWebchatBrokenReconnectCount = 0;
  internalWebchatBrokenReconnectWindowStartedAt = undefined;
}

async function probeAuthReadySyncLiveness() {
  const [currentUser, chats] = await Promise.all([
    callApi('fetchCurrentUser'),
    callApi('fetchChats', {
      limit: 1,
      archived: false,
      withPinned: true,
    }),
  ]);

  return Boolean(currentUser && chats);
}

function triggerApiReconnect(actions: RequiredGlobalActions) {
  const latestGlobal = getGlobal();
  const nextGlobal = { ...latestGlobal, isSynced: false };
  setGlobal(nextGlobal);

  onUpdateConnectionState(nextGlobal, actions, {
    '@type': 'updateConnectionState',
    connectionState: 'connectionStateConnecting',
  });
  actions.initApi();
}

function schedulePostAuthSyncProbe(actions: RequiredGlobalActions) {
  clearPostAuthSyncProbeTimer();
  postAuthSyncProbeTimeout = setTimeout(() => {
    postAuthSyncProbeTimeout = undefined;

    const latestGlobal = getGlobal();
    if (
      latestGlobal.auth.state !== 'authorizationStateReady'
      || latestGlobal.connectionState !== 'connectionStateReady'
      || latestGlobal.isSynced
    ) {
      return;
    }

    void (async () => {
      const isLive = await probeAuthReadySyncLiveness();
      if (isLive) {
        return;
      }

      triggerApiReconnect(actions);
    })();
  }, POST_AUTH_SYNC_PROBE_DELAY_MS);
}

function syncConnectingSelfHeal(global: GlobalState, actions: RequiredGlobalActions) {
  const isBrowserOnline = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
  const isConnecting = global.connectionState === 'connectionStateConnecting';

  if (!isConnecting || !isBrowserOnline) {
    resetConnectingSelfHealState();
    return;
  }

  if (!connectingSince) {
    connectingSince = Date.now();
  }

  clearConnectingSelfHealTimer();

  const remainingMs = Math.max(0, CONNECTING_SELF_HEAL_DELAY_MS - (Date.now() - connectingSince));
  connectingSelfHealTimeout = setTimeout(() => {
    connectingSelfHealTimeout = undefined;

    const latestGlobal = getGlobal();
    const isLatestBrowserOnline = typeof navigator === 'undefined' ? true : navigator.onLine !== false;

    if (!shouldTriggerConnectingSelfHeal({
      connectionState: latestGlobal.connectionState,
      authState: latestGlobal.auth.state,
      isBrowserOnline: isLatestBrowserOnline,
      connectingSince,
      now: Date.now(),
      attempts: connectingSelfHealAttempts,
    })) {
      syncConnectingSelfHeal(latestGlobal, actions);
      return;
    }

    connectingSelfHealAttempts += 1;

    if (
      latestGlobal.auth.state === 'authorizationStateReady'
      && Boolean(latestGlobal.currentUserId)
      && !shouldUsePublicWebchatHttpFallback()
      && isPublicWebchatHttpFallbackEligible()
    ) {
      markPublicWebchatHttpFallback();
    }

    const nextGlobal = { ...latestGlobal, isSynced: false };
    setGlobal(nextGlobal);
    const shouldKeepCurrentSessionAuth = latestGlobal.auth.state === 'authorizationStateReady'
      && Boolean(latestGlobal.currentUserId)
      && !shouldResetBrokenSaasHandoffAuth();
    if (!shouldKeepCurrentSessionAuth && !maybeResetBrokenSaasHandoffAuthSession()) {
      clearCurrentSlotAuthKeysOnly();
    }
    actions.initApi();
    syncConnectingSelfHeal(nextGlobal, actions);
  }, remainingMs);
}

/**
 * Establish a storage session cookie for tenant-isolated file access.
 * Sends server-verifiable identity proof to the gateway, which resolves
 * the current user/tenant and sets an HttpOnly cookie scoped to /storage/.
 */
async function initStorageSession(userId: string) {
  const body = buildStorageSessionRequestBody(userId);
  if (!body) {
    return;
  }
  if (
    'authSessionProof' in body
    && isWorkGoogleBridgeCallback(
      window.location.search,
      window.location.pathname,
      window.location.hostname,
    )
  ) {
    return;
  }
  try {
    await fetch('/api/storage-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    });
  } catch {
    // Non-critical — storage links will still work via HMAC-only mode
  }
}

async function fetchSaasBootstrapAuthState() {
  try {
    const response = await fetch('/api/run/saas-bootstrap', {
      credentials: 'same-origin',
    });
    if (!response.ok) {
      return undefined;
    }

    const payload = await response.json().catch(() => undefined) as { authState?: string } | undefined;
    return typeof payload?.authState === 'string' ? payload.authState : undefined;
  } catch {
    return undefined;
  }
}

function markStorageSessionRepairAttempted() {
  hasAttemptedStorageSessionRepair = true;
}

async function maybeRepairMissingStorageSession(currentUserId?: string) {
  if (!currentUserId || hasAttemptedStorageSessionRepair) {
    return;
  }

  const latestGlobal = getGlobal();
  if (
    latestGlobal.auth.state !== 'authorizationStateReady'
    || latestGlobal.connectionState !== 'connectionStateReady'
  ) {
    return;
  }

  const authState = await fetchSaasBootstrapAuthState();
  if (authState === 'authenticated') {
    return;
  }
  if (authState !== 'anonymous') {
    return;
  }

  markStorageSessionRepairAttempted();
  await initStorageSession(currentUserId);
  await fetchSaasBootstrapAuthState();
}

async function maybeProvisionRunSaasTenant(userId: string, firstName?: string) {
  void userId;
  void firstName;
  // Fail closed: browser currentUserId is not a server-verifiable identity source
  // for SaaS tenant provisioning. Keep the bootstrap hint in client storage, but do
  // not attempt /api/run/provision until a server-bound user session exists.
  return;
}

function shouldResetBrokenSaasHandoffAuth(search = window.location.search) {
  const searchParams = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  return Boolean(searchParams.get('ownerAccessToken') || window.location.hash.includes('tgWebAuthToken='));
}

function markBrokenSaasHandoffAuthReset() {
  try {
    window.sessionStorage.setItem(SAAS_HANDOFF_AUTH_RESET_KEY, '1');
  } catch {
    // ignore storage failures
  }
}

function hasMarkedBrokenSaasHandoffAuthReset() {
  try {
    return window.sessionStorage.getItem(SAAS_HANDOFF_AUTH_RESET_KEY) === '1';
  } catch {
    return false;
  }
}

function maybeResetBrokenSaasHandoffAuthSession() {
  if (!shouldResetBrokenSaasHandoffAuth() || hasMarkedBrokenSaasHandoffAuthReset()) {
    return false;
  }

  clearStoredAuthKeysOnly();
  if (window.location.hash.includes('tgWebAuthToken=')) {
    clearStoredSession(ACCOUNT_SLOT);
  }
  markBrokenSaasHandoffAuthReset();
  return true;
}

function shouldUseBrokenReconnectRecovery(global: GlobalState) {
  return isPublicSaasOwnerAccessTokenLane()
    || shouldResetBrokenSaasHandoffAuth()
    || (
      Boolean(global.currentUserId)
      && global.auth?.state === 'authorizationStateReady'
      && !shouldUseInternalWebchatHttpFallback()
      && isInternalWebchatHttpFallbackEligible()
    )
    || (
      !global.currentUserId
      && global.auth?.state !== 'authorizationStateReady'
      && !shouldUsePublicWebchatHttpFallback()
      && isPublicWebchatHttpFallbackEligible()
    );
}

addActionHandler('apiUpdate', (global, actions, update): ActionReturnType => {
  switch (update['@type']) {
    case 'updateApiReady':
      onUpdateApiReady(global);
      break;

    case 'updateAuthorizationState':
      onUpdateAuthorizationState(global, actions, update);
      break;

    case 'updateAuthorizationError':
      onUpdateAuthorizationError(global, update);
      break;

    case 'updateUserAlreadyAuthorized':
      onUpdateUserAlreadyAuthorized(global, update);
      break;

    case 'updateWebAuthTokenFailed':
      onUpdateWebAuthTokenFailed(global, update);
      break;

    case 'updatePasskeyOption':
      onUpdatePasskeyOption(global, update);
      break;

    case 'updateConnectionState':
      onUpdateConnectionState(global, actions, update);
      break;

    case 'updateSession':
      onUpdateSession(global, actions, update);
      break;

    case 'updateServerTimeOffset':
      onUpdateServerTimeOffset(update);
      break;

    case 'updateCurrentUser':
      onUpdateCurrentUser(global, update);
      break;

    case 'requestReconnectApi':
      emitReconnectTrace('request_reconnect_api', {
        authState: global.auth.state,
        connectionState: global.connectionState,
      });
      global = { ...global, isSynced: false };
      setGlobal(global);

      onUpdateConnectionState(global, actions, {
        '@type': 'updateConnectionState',
        connectionState: 'connectionStateConnecting',
      });
      if (
        global.auth.state !== 'authorizationStateWaitPhoneNumber'
        && global.auth.state !== 'authorizationStateWaitCode'
        && global.auth.state !== 'authorizationStateWaitPassword'
        && global.auth.state !== 'authorizationStateWaitRegistration'
      ) {
        emitReconnectTrace('request_reconnect_api_init', {
          authState: global.auth.state,
        });
        actions.initApi();
      }
      break;

    case 'requestSync':
      actions.sync();
      break;

    case 'updateFetchingDifference':
      global = { ...global, isFetchingDifference: update.isFetching };
      setGlobal(global);
      break;

    case 'error': {
      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        const paymentShippingError = getShippingError(update.error);
        if (paymentShippingError) {
          actions.addPaymentError({ error: paymentShippingError, tabId });
        } else if (shouldClosePaymentModal(update.error)) {
          actions.closePaymentModal({ tabId });
        } else if (actions.showDialog) {
          actions.showDialog({ data: update.error, tabId });
        }
      });

      break;
    }

    case 'notSupportedInFrozenAccount': {
      actions.showNotification({
        title: {
          key: 'NotificationTitleNotSupportedInFrozenAccount',
        },
        message: {
          key: 'NotificationMessageNotSupportedInFrozenAccount',
        },
        tabId: getCurrentTabId(),
      });
      break;
    }
  }
});

function onUpdateApiReady<T extends GlobalState>(global: T) {
  void oldSetLanguage(selectSharedSettings(global).language as LangCode);
}

function onUpdateAuthorizationState<T extends GlobalState>(
  global: T,
  actions: RequiredGlobalActions,
  update: ApiUpdateAuthorizationState,
) {
  clearSmsLoginStuckTimer();
  const wasAuthReady = global.auth.state === 'authorizationStateReady';
  const previousAuthState = global.auth.state;
  const previousPhoneNumber = global.auth.phoneNumber;
  let authState = update.authorizationState;
  if (authState === 'authorizationStateWaitQrCode') {
    try {
      const hasDebugPhoneAuth = window.sessionStorage.getItem(DEBUG_PHONE_AUTH_MARKER) === '1';
      const pendingAutoCode = (window as any).__pendingAutoCode;
      if (hasDebugPhoneAuth && pendingAutoCode?.code) {
        authState = 'authorizationStateWaitCode';
      }
    } catch {
      // ignore storage access errors
    }
  }

  global = updateAuth(global, {
    state: authState,
    isLoading: false,
  });
  setGlobal(global, { forceOutdated: true });
  global = getGlobal();

  switch (authState) {
    case 'authorizationStateLoggingOut':
      void forceWebsync(false);

      global = updateAuth(global, {
        isLoggingOut: true,
      });
      setGlobal(global, { forceOutdated: true });
      break;
    case 'authorizationStateWaitCode':
      clearBrokenReconnectTimer();
      clearPreferredAuthRestartMode();
      if (previousAuthState === 'authorizationStateWaitPhoneNumber') {
        reportSmsLoginEvent('send_code_succeeded', {
          authState: 'authorizationStateWaitCode',
          prevAuthState: previousAuthState,
          connectionState: global.connectionState,
          phoneNumber: previousPhoneNumber,
        });
      }
      global = updateAuth(global, {
        isCodeViaApp: update.isCodeViaApp,
        canRequestSms: update.canRequestSms,
      });
      setGlobal(global);
      break;
    case 'authorizationStateWaitPassword':
      clearBrokenReconnectTimer();
      clearPreferredAuthRestartMode();
      global = updateAuth(global, {
        hint: update.hint,
      });

      if (update.noReset) {
        global = updateAuth(global, {
          hasWebAuthTokenPasswordRequired: true,
        });
      }

      setGlobal(global);
      break;
    case 'authorizationStateWaitQrCode':
      global = updateAuth(global, {
        isLoadingQrCode: false,
        qrCode: update.qrCode,
      });
      setGlobal(global);
      break;
    case 'authorizationStateReady': {
      clearBrokenReconnectTimer();
      clearPreferredAuthRestartMode();
      if (wasAuthReady) {
        break;
      }

      reportAuthSessionEvent('authenticated_ready', {
        authState: 'authorizationStateReady',
        prevAuthState: previousAuthState,
        userId: global.currentUserId ? String(global.currentUserId) : undefined,
        connectionState: global.connectionState,
        finalPath: window.location.pathname,
      });
      if (
        previousAuthState === 'authorizationStateWaitCode'
        || previousAuthState === 'authorizationStateWaitPassword'
        || previousAuthState === 'authorizationStateWaitRegistration'
      ) {
        reportSmsLoginEvent('sign_in_succeeded', {
          authState: 'authorizationStateReady',
          prevAuthState: previousAuthState,
          userId: global.currentUserId ? String(global.currentUserId) : undefined,
          connectionState: global.connectionState,
          phoneNumber: previousPhoneNumber,
          finalPath: window.location.pathname,
        });
      }

      void forceWebsync(true);

      global = updateAuth(global, {
        isLoggingOut: false,
      });
      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        global = updateTabState(global, {
          inactiveReason: undefined,
        }, tabId);
      });
      setGlobal(global);

      // For SaaS handoff lanes, ensure canonical tenant truth exists before
      // establishing the storage-session cookie that consumes it.
      if (global.currentUserId) {
        const currentUser = selectUser(global, global.currentUserId);
        void (async () => {
          await maybeProvisionRunSaasTenant(global.currentUserId!, currentUser?.firstName);
          await initStorageSession(global.currentUserId!);
          schedulePostAuthSyncProbe(actions);
        })();
      }

      break;
    }
  }

  if (
    wasAuthReady
    && authState !== 'authorizationStateReady'
    && (
      authState === 'authorizationStateWaitPhoneNumber'
      || authState === 'authorizationStateWaitCode'
      || authState === 'authorizationStateWaitPassword'
      || authState === 'authorizationStateWaitRegistration'
      || authState === 'authorizationStateWaitQrCode'
      || authState === 'authorizationStateWaitGoogleAuth'
    )
  ) {
    reportAuthSessionEvent('returned_to_login_after_authenticated', {
      authState,
      prevAuthState: previousAuthState,
      userId: global.currentUserId ? String(global.currentUserId) : undefined,
      connectionState: global.connectionState,
      finalPath: window.location.pathname,
    });
  }
}

function maskPhoneForAuthLog(phoneNumber?: string) {
  const digits = String(phoneNumber || '').replace(/[^\d]+/g, '');
  if (!digits) return null;
  if (digits.startsWith('86') && digits.length >= 6) {
    return `+86 ***${digits.slice(-4)}`;
  }
  if (digits.length > 4) {
    const prefix = digits.length > 10 ? `+${digits.slice(0, digits.length - 10)}` : '';
    return `${prefix} ***${digits.slice(-4)}`.trim();
  }
  return `***${digits}`;
}

function getAuthLoginSurface(authState: GlobalState['auth']['state']) {
  if (authState === 'authorizationStateWaitPhoneNumber') return 'phone';
  if (authState === 'authorizationStateWaitQrCode') return 'qr';
  return 'unknown';
}

function onUpdateAuthorizationError<T extends GlobalState>(global: T, update: ApiUpdateAuthorizationError) {
  clearSmsLoginStuckTimer();
  if (update.errorCode === 'PASSKEY_CREDENTIAL_NOT_FOUND') {
    getActions().showNotification({
      message: update.errorKey,
      tabId: getCurrentTabId(),
    });
    return;
  }

  const authState = global.auth.state;
  const loginSurface = getAuthLoginSurface(authState);
  const phoneMask = maskPhoneForAuthLog(global.auth.phoneNumber);
  const errorKeyValue = typeof update.errorKey === 'object' && update.errorKey !== null
    ? ('key' in update.errorKey ? update.errorKey.key ?? null : null)
    : update.errorKey ?? null;

  reportClientError({
    kind: 'runtime',
    mode: 'immediate',
    message: `[auth_login] authorization_error code=${update.errorCode || 'unknown'} state=${authState || 'unknown'} surface=${loginSurface}`,
    dedupeKey: [
      'auth_login',
      update.errorCode || 'unknown',
      authState || 'unknown',
      loginSurface,
      phoneMask || 'no-phone',
    ].join(':'),
    metadata: {
      source: 'auth_login',
      transition: 'authorization_error',
      authState: authState || null,
      errorCode: update.errorCode || null,
      errorKey: errorKeyValue,
      connectionState: global.connectionState || null,
      loginSurface,
      phoneMask,
    },
  });

  if (
    authState === 'authorizationStateWaitPhoneNumber'
    || authState === 'authorizationStateWaitCode'
    || authState === 'authorizationStateWaitPassword'
    || authState === 'authorizationStateWaitRegistration'
  ) {
    reportSmsLoginEvent(
      authState === 'authorizationStateWaitPhoneNumber' ? 'send_code_failed' : 'sign_in_failed',
      {
        authState,
        connectionState: global.connectionState,
        phoneNumber: global.auth.phoneNumber,
        errorType: update.errorCode || undefined,
        errorMessage: errorKeyValue ? String(errorKeyValue) : undefined,
      },
    );
  }

  global = updateAuth(global, {
    isLoading: false,
    errorKey: update.errorKey,
    errorCode: update.errorCode,
  });
  setGlobal(global);
}

function onUpdateUserAlreadyAuthorized<T extends GlobalState>(global: T, update: ApiUpdateUserAlreadyAuthorized) {
  const { userId } = update;
  if (global.currentUserId === userId) return;

  const accounts = getAccountsInfo();
  const slot = Object.entries(accounts).find(([_, info]) => info.userId === userId)?.[0];
  if (!slot) return;
  window.location.replace(buildAlreadyAuthorizedRedirectUrl(window.location.href, getAccountSlotUrl(Number(slot))));
}

export function buildAlreadyAuthorizedRedirectUrl(currentHref: string, slotUrl: string): string {
  const currentUrl = new URL(currentHref);
  const targetUrl = new URL(slotUrl, currentUrl.origin);
  currentUrl.searchParams.forEach((value, key) => {
    if (!targetUrl.searchParams.has(key)) {
      targetUrl.searchParams.set(key, value);
    }
  });
  if (currentUrl.hash && !targetUrl.hash) {
    targetUrl.hash = currentUrl.hash;
  }
  return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
}

function onUpdateWebAuthTokenFailed<T extends GlobalState>(
  global: T,
  update: Extract<ApiUpdate, { '@type': 'updateWebAuthTokenFailed' }>,
) {
  if (shouldPreserveWebAuthTokenLane(update.errorCode)) {
    return;
  }

  clearWebTokenAuth();
  clearPendingWebAuthToken();

  global = updateAuth(global, {
    errorKey: 'Google sign-in failed' as any,
    errorCode: update.errorCode,
    hasWebAuthTokenFailed: true,
  });
  setGlobal(global);
}

function shouldPreserveWebAuthTokenLane(errorCode?: string) {
  return [
    'AUTH_KEY_UNREGISTERED',
    'Not connected',
    'HttpStream was closed',
    'WebSocket was closed',
    'Failed to fetch',
    'Step 3 invalid new nonce hash',
  ].includes(errorCode || '');
}

function onUpdatePasskeyOption<T extends GlobalState>(global: T, update: ApiUpdatePasskeyOption) {
  global = updateAuth(global, {
    passkeyOption: update.option,
  });
  setGlobal(global);
}

function onUpdateConnectionState<T extends GlobalState>(
  global: T, actions: RequiredGlobalActions, update: ApiUpdateConnectionState,
) {
  // In debug builds, action handlers may receive a stale global snapshot carrying an
  // expired DEBUG_randomId. Refresh from the live store before mutating connection state.
  global = getGlobal() as T;
  const { connectionState } = update;

  const tabState = selectTabState(global, getCurrentTabId());
  if (connectionState === 'connectionStateReady' && tabState.isMasterTab && tabState.multitabNextAction) {
    // @ts-ignore
    actions[tabState.multitabNextAction.action](tabState.multitabNextAction.payload);
    actions.clearMultitabNextAction({ tabId: tabState.id });
  }

  if (connectionState === 'connectionStateReady') {
    clearBrokenReconnectTimer();
    brokenReconnectAttempts = 0;
  }

  if (connectionState === global.connectionState) {
    return;
  }

  reportConnectionStateTransition(global.connectionState, connectionState);

  global = {
    ...global,
    connectionState,
  };
  global = degradeSyncFlagsForConnection(global, connectionState);
  setGlobal(global);
  syncConnectingSelfHeal(global, actions);

  if (connectionState === 'connectionStateReady') {
    resetConnectingSelfHealState();
    if (global.currentUserId || global.auth?.state === 'authorizationStateReady') {
      resetPublicWebchatBrokenStartupState();
      resetInternalWebchatBrokenReconnectState();
    }
    if (global.currentUserId && global.auth?.state === 'authorizationStateReady') {
      void maybeRepairMissingStorageSession(String(global.currentUserId));
    }
    flushPendingMessageReactionLoads(global, actions);
  }

  if (global.isSynced) {
    const channelStackIds = Object.values(global.byTabId)
      .flatMap((tab) => tab.messageLists || [])
      .filter(Boolean)
      .map((messageList) => messageList.chatId)
      .filter((chatId) => {
        const chat = global.chats.byId[chatId];
        return chat && (isChatChannel(chat) || isChatSuperGroup(chat));
      });
    if (connectionState === 'connectionStateReady' && channelStackIds.length) {
      unique(channelStackIds).forEach((chatId) => {
        actions.requestChannelDifference({ chatId });
      });
    }
  }

  if (connectionState === 'connectionStateBroken') {
    clearConnectingSelfHealTimer();
    connectingSince = undefined;
    if (global.auth?.hasWebAuthTokenFailed) {
      return;
    }
    if (
      global.auth?.state === 'authorizationStateWaitPhoneNumber'
      || global.auth?.state === 'authorizationStateWaitCode'
      || global.auth?.state === 'authorizationStateWaitPassword'
      || global.auth?.state === 'authorizationStateWaitRegistration'
    ) {
      clearBrokenReconnectTimer();
      brokenReconnectAttempts = 0;
      return;
    }
    if (brokenReconnectTimeout !== undefined) {
      return;
    }

    if (!shouldUseBrokenReconnectRecovery(global)) {
      clearBrokenReconnectTimer();
      brokenReconnectAttempts = 0;
      actions.signOut({ forceInitApi: true });
      return;
    }

    if (brokenReconnectAttempts < MAX_BROKEN_RECONNECT_ATTEMPTS) {
      brokenReconnectAttempts++;
      maybeResetBrokenSaasHandoffAuthSession();
      if (
        !global.currentUserId
        && global.auth?.state !== 'authorizationStateReady'
        && !isPublicSaasOwnerAccessTokenLane()
        && !shouldUsePublicWebchatHttpFallback()
        && isPublicWebchatHttpFallbackEligible()
        && notePublicWebchatBrokenStartup()
      ) {
        markPublicWebchatHttpFallback();
      }
      if (
        Boolean(global.currentUserId)
        && global.auth?.state === 'authorizationStateReady'
        && !shouldUseInternalWebchatHttpFallback()
        && isInternalWebchatHttpFallbackEligible()
        && noteInternalWebchatBrokenReconnect()
      ) {
        markInternalWebchatHttpFallback();
      }
      // Exponential backoff: 1s, 2s, 4s, 8s… capped at 30s
      const delay = Math.min(1000 * (2 ** (brokenReconnectAttempts - 1)), 30_000);
      global = { ...global, isSynced: false };
      setGlobal(global);

      onUpdateConnectionState(global, actions, {
        '@type': 'updateConnectionState',
        connectionState: 'connectionStateConnecting',
      });
      brokenReconnectTimeout = setTimeout(() => {
        brokenReconnectTimeout = undefined;
        actions.initApi();
      }, delay);
    } else {
      brokenReconnectAttempts = 0;
      actions.signOut({ forceInitApi: true });
    }
  }
}

function onUpdateSession<T extends GlobalState>(global: T, actions: RequiredGlobalActions, update: ApiUpdateSession) {
  const { sessionData } = update;
  const { rememberMe, state } = global.auth;
  const isEmpty = !sessionData || !sessionData.mainDcId;

  const isTest = sessionData?.isTest;
  if (isTest) {
    global = {
      ...global,
      config: {
        ...global.config,
        isTestServer: isTest,
      },
    };
    setGlobal(global);
  }

  if (!rememberMe || state !== 'authorizationStateReady' || isEmpty) {
    return;
  }

  actions.saveSession({ sessionData });
}

function onUpdateServerTimeOffset(update: ApiUpdateServerTimeOffset) {
  setServerTimeOffset(update.serverTimeOffset);
}

function onUpdateCurrentUser<T extends GlobalState>(global: T, update: ApiUpdateCurrentUser) {
  const { currentUser, currentUserFullInfo } = update;

  global = {
    ...updateUser(global, currentUser.id, currentUser),
    currentUserId: currentUser.id,
  };
  if (global.auth.state !== 'authorizationStateReady') {
    global = updateAuth(global, {
      state: 'authorizationStateReady',
      isLoading: false,
    });
  }
  // @ts-expect-error Upstream keeps currentUserFullInfo optional on this update path.
  global = updateUserFullInfo(global, currentUser.id, currentUserFullInfo);

  if (
    shouldForceTranslationDebugForUser(currentUser.id)
    && !global.sharedState.settings.shouldCollectTranslationDebugLogs
  ) {
    global = updateSharedState(global, {
      settings: {
        ...global.sharedState.settings,
        shouldCollectTranslationDebugLogs: true,
      },
    });
    initDebugConsole();
    void setShouldEnableDebugLog(true);
    logTranslationDebugEvent(true, 'translation_debug_forced_user', {
      currentUserId: currentUser.id,
      browserLocale: navigator.language,
      browserLocales: navigator.languages,
      htmlLang: document.documentElement.lang,
      appLanguage: global.sharedState.settings.language,
      translationLanguage: global.settings.byKey.translationLanguage,
      targetLanguage: selectTranslationLanguage(global),
    });
  }

  setGlobal(global);

  updateSessionUserId(currentUser.id);

  if (global.auth.state === 'authorizationStateReady' && global.connectionState === 'connectionStateReady') {
    void maybeRepairMissingStorageSession(String(currentUser.id));
  }
}
